import type { Ref } from "react";
import React, { useId, useImperativeHandle } from "react";

import cn from "classnames";

import PriceSVGMask from "./components/SVGMask/PriceSVGMask";
import {
    PRICE_DISPLAY_NAME,
    PRICE_SVG_MASK_TEST_ID,
    PRICE_TEST_ID,
} from "./vars";
import {
    Box,
    type BoxComponentWithDefault,
    boxForwardRef,
} from "../../helpers/common/Box/Box";
import { useResizeObservable } from "../../hooks/useResizeObservable";
import { getClipPathStyle } from "../../utils/ui/svg";

import type { PriceProps } from "./types";

import classes from "./Price.styl";

/**
 * Компонент, который сообщает о стоимости какого-то товара или подарка.
 *
 * В качестве `as` ожидаем, что будет передан 'button' или 'a'.
 * Более подробную информацию можно найти [тут](/docs/документация-разработчикам-дс-инструкция-по-разработке-полиморфных-компонентов--docs).
 *
 * <a href="https://www.figma.com/design/vTg3KAvmAXA9LigeyiqCWL/%F0%9F%92%A0-Base-Components?m=auto&node-id=11284-37468&t=HJmjzZIJxIFeO9EU-1" target="_blank">Спека</a>
 */
export const Price = boxForwardRef<
    BoxComponentWithDefault<"button", PriceProps>
>(
    (
        {
            as = "button",
            state = "enabled",
            animation = false,
            "data-tsid": tsid,
            label,
            customVarsStyles,
            ...props
        },
        ref: Ref<HTMLElement | null>,
    ) => {
        const svgMaskId = useId();
        const priceRef = React.useRef<HTMLDivElement | null>(null);

        useImperativeHandle(ref, () => priceRef.current);

        const { width } = useResizeObservable({
            observableElementRef: priceRef,
        });

        const styleProps = {
            ...getClipPathStyle(svgMaskId),
        };

        const priceClasses = cn(classes["price-wrapper"], customVarsStyles, {
            [classes[`price-wrapper__state-${state}`]]: state,
            [classes[`price__animation`]]: animation,
        });

        const priceInnerClasses = cn(classes["price-inner"], {
            [classes[`price-inner__state-${state}`]]: state,
        });

        return (
            <Box
                {...props}
                as={as}
                data-tsid={tsid || PRICE_TEST_ID}
                data-uikit={PRICE_DISPLAY_NAME}
                className={priceClasses}
                ref={priceRef}
            >
                <span style={styleProps} className={priceInnerClasses}>
                    {label}
                    <PriceSVGMask
                        width={width}
                        id={svgMaskId}
                        data-tsid={PRICE_SVG_MASK_TEST_ID}
                    />
                </span>
            </Box>
        );
    },
);

Price.displayName = PRICE_DISPLAY_NAME;

export default Price;
